import React, { useState, useEffect } from 'react';
import {
  Users,
  MessageSquare,
  FileText,
  Mail,
  BarChart3,
  Brain,
  Settings,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Building2,
  ArrowLeft,
  LogOut,
  X
} from 'lucide-react';

interface QuotationSuggestion {
  suggestion_id: string;
  customer_id: string;
  company: string;
  timestamp: string;
  status: string;
  budget: string;
  quantity: string;
  quotation_data?: any;
}

interface SuggestionDetails {
  customer: string;
  requirements: string;
  budget: string;
  occasion: string;
  strategies: Array<{
    name: string;
    description: string;
    cost: string;
    items: string[];
    variance?: string;
    reasoning?: string;
  }>;
}

interface EmailLog {
  id: string;
  to: string;
  subject: string;
  template: string;
  status: string;
  timestamp: string;
  quotation_id: string;
}

interface ControlCenterProps {
  onBack?: () => void;
  onLogout?: () => void;
}

export const ControlCenter: React.FC<ControlCenterProps> = ({ onBack, onLogout }) => {
  const [activeTab, setActiveTab] = useState('chatbot');
  const [suggestions, setSuggestions] = useState<QuotationSuggestion[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<string>('');
  const [suggestionDetails, setSuggestionDetails] = useState<SuggestionDetails | null>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  
  // Quotation settings
  const [showDraftQuotation, setShowDraftQuotation] = useState<boolean>(true);
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState<boolean>(true);
  const [_settingsMessage, setSettingsMessage] = useState('');
  
  // Chatbot settings
  const [requiredFields, setRequiredFields] = useState({
    company_name: true,
    customer_email: true,
    budget_per_pack: true,
    number_of_packs: true,
    occasion: true,
    special_requests: false
  });
  const [thankYouNote, setThankYouNote] = useState('Thank you for your enquiry! We will get back to you shortly with a customized quotation.');
  const [chatbotMessage, setChatbotMessage] = useState('');

  // Business Units Connector settings
  const [businessUnits, setBusinessUnits] = useState([
    { id: 'sales', name: 'Sales Team', email: 'sales@company.com', active: true, conditions: [] },
    { id: 'premium', name: 'Premium Division', email: 'premium@company.com', active: true, conditions: [] },
    { id: 'corporate', name: 'Corporate Events', email: 'corporate@company.com', active: false, conditions: [] },
  ]);
  const [routingRules, setRoutingRules] = useState({
    default_unit: 'sales',
    budget_thresholds: {
      premium: 100, // Orders above $100/pack go to premium
      corporate: 1000 // Orders above $1000 total go to corporate
    },
    occasion_routing: {
      'corporate': 'corporate',
      'festival': 'premium',
      'wedding': 'premium'
    }
  });
  const [connectorMessage, setConnectorMessage] = useState('');

  // Email Management settings
  const [emailConfig, setEmailConfig] = useState({
    smtp_server: 'smtp.gmail.com',
    smtp_port: 587,
    sender_email: 'mary@printngift.com',
    sender_name: 'Mary - PrintnGift Sales Assistant',
    use_tls: true
  });
  const [emailTemplates, setEmailTemplates] = useState([
    {
      id: 'quotation_ready',
      name: 'Quotation Ready',
      subject: 'Your Custom Quotation is Ready - PrintnGift',
      content: 'Dear {{customer_name}},\n\nThank you for your interest in PrintnGift! Your customized quotation is ready.\n\nBest regards,\nMary'
    },
    {
      id: 'follow_up',
      name: 'Follow Up',
      subject: 'Following up on your quotation - PrintnGift',
      content: 'Dear {{customer_name}},\n\nI hope this email finds you well. I wanted to follow up on the quotation we sent.\n\nBest regards,\nMary'
    }
  ]);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [emailMessage, setEmailMessage] = useState('');

  // Fetch quotation suggestions
  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/quotation-suggestions');
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      // Mock data for demo
      setSuggestions([
        {
          suggestion_id: 'SUGG-20250816143022',
          customer_id: 'victor@abcliner.com',
          company: 'ABC Liner',
          timestamp: '2025-08-16 14:30',
          status: 'pending_staff_review',
          budget: '$25/pack',
          quantity: '50 packs'
        },
        {
          suggestion_id: 'SUGG-20250816142815',
          customer_id: 'sarah@techcorp.com',
          company: 'TechCorp',
          timestamp: '2025-08-16 14:28',
          status: 'pending_staff_review',
          budget: '$45/pack',
          quantity: '100 packs'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // View suggestion details
  const viewSuggestionDetails = async (suggestionId: string) => {
    try {
      const response = await fetch(`/api/quotation-suggestions/${suggestionId}`);
      if (response.ok) {
        const data = await response.json();
        setSuggestionDetails(data);
      } else {
        // Mock data for demo
        setSuggestionDetails({
          customer: 'victor@abcliner.com (ABC Liner)',
          requirements: 'Corporate gifts for festival celebration',
          budget: '$25 per pack × 50 packs = $1,250 total',
          occasion: 'Festival',
          strategies: [
            {
              name: 'Exact Budget Match',
              description: 'Precisely targets $25.00 per pack budget',
              cost: '$25.00/pack | $1,250 total',
              variance: '0.0% variance',
              reasoning: 'Selected items that total exactly $25.00 per pack',
              items: ['Custom Branded Tumbler ($25.00)']
            },
            {
              name: 'Premium Value',
              description: 'High-quality selection at $23.75 per pack',
              cost: '$23.75/pack | $1,187.50 total',
              variance: '5.0% under budget',
              reasoning: 'Premium products within 95% of maximum budget',
              items: ['Executive Gift Set ($23.75)']
            },
            {
              name: 'Budget Optimized',
              description: 'Maximum savings at $21.25 per pack',
              cost: '$21.25/pack | $1,062.50 total',
              variance: '15.0% savings',
              reasoning: 'Cost-effective selection saving 15% from target budget',
              items: ['Value Corporate Pack ($21.25)']
            },
            {
              name: 'Balanced Mix',
              description: 'Even distribution across price ranges',
              cost: '$24.50/pack | $1,225 total',
              variance: '2.0% under budget',
              reasoning: 'Mix of low, medium, and high-value items for variety',
              items: ['Premium Drinkware & Tech Bundle ($24.50)']
            },
            {
              name: 'Conservative Choice',
              description: 'Safe selection at $22.50 per pack',
              cost: '$22.50/pack | $1,125 total',
              variance: '10.0% under budget',
              reasoning: 'Conservative approach with 10% budget cushion',
              items: ['Corporate Essentials Pack ($22.50)']
            },
            {
              name: 'Maximum Value',
              description: 'Highest quality at $30.00 per pack',
              cost: '$30.00/pack | $1,500 total',
              variance: '20.0% premium',
              reasoning: 'Premium selection utilizing extended budget allowance',
              items: ['Luxury Executive Collection ($30.00)']
            },
            {
              name: 'Alternative Mix',
              description: 'Diverse product selection at $25.00 per pack',
              cost: '$25.00/pack | $1,250 total',
              variance: '0.0% variance',
              reasoning: 'Alternative selection focusing on product diversity',
              items: ['Mixed Category Bundle ($25.00)']
            }
          ]
        });
      }
    } catch (error) {
      console.error('Failed to fetch suggestion details:', error);
    }
  };

  // Accept suggestion
  const acceptSuggestion = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/quotation-suggestions/${selectedSuggestion}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ strategy_index: selectedStrategy }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessage(`✅ Suggestion accepted! Quotation ${data.quotation_id} moved to approval pipeline.`);
        fetchSuggestions(); // Refresh the list
      } else {
        setMessage('❌ Failed to accept suggestion');
      }
    } catch (error) {
      setMessage('❌ Error accepting suggestion');
    } finally {
      setLoading(false);
    }
  };

  // Save settings to localStorage and backend
  const _saveSettings = async () => {
    try {
      setLoading(true);
      const settings = {
        show_draft_quotation: showDraftQuotation,
        email_notification_enabled: emailNotificationEnabled
      };
      
      // Save to localStorage for immediate use
      localStorage.setItem('quotationSettings', JSON.stringify(settings));
      
      // Send to backend
      const response = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setSettingsMessage('✅ Settings saved successfully!');
      } else {
        setSettingsMessage('⚠️ Settings saved locally, backend update failed');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSettingsMessage('⚠️ Settings saved locally, backend update failed');
    } finally {
      setLoading(false);
      setTimeout(() => setSettingsMessage(''), 3000);
    }
  };

  // Save chatbot settings
  const _saveChatbotSettings = async () => {
    try {
      setLoading(true);
      const settings = {
        required_fields: requiredFields,
        thank_you_note: thankYouNote
      };
      
      // Save to localStorage for immediate use
      localStorage.setItem('chatbotSettings', JSON.stringify(settings));
      
      // Send to backend
      const response = await fetch('/api/admin/chatbot-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setChatbotMessage('✅ Chatbot settings saved successfully!');
      } else {
        setChatbotMessage('⚠️ Settings saved locally, backend update failed');
      }
    } catch (error) {
      console.error('Error saving chatbot settings:', error);
      setChatbotMessage('⚠️ Settings saved locally, backend update failed');
    } finally {
      setLoading(false);
      setTimeout(() => setChatbotMessage(''), 3000);
    }
  };

  // Save all chatbot settings (including display settings)
  const saveAllChatbotSettings = async () => {
    try {
      setLoading(true);
      
      // Save quotation display settings
      const quotationSettings = {
        show_draft_quotation: showDraftQuotation,
        email_notification_enabled: emailNotificationEnabled
      };
      
      // Save chatbot configuration settings
      const chatbotSettings = {
        required_fields: requiredFields,
        thank_you_note: thankYouNote
      };
      
      // Save to localStorage for immediate use
      localStorage.setItem('quotationSettings', JSON.stringify(quotationSettings));
      localStorage.setItem('chatbotSettings', JSON.stringify(chatbotSettings));
      
      // Send both to backend
      const [quotationResponse, chatbotResponse] = await Promise.all([
        fetch('/api/admin/settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(quotationSettings)
        }),
        fetch('/api/admin/chatbot-settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(chatbotSettings)
        })
      ]);
      
      if (quotationResponse.ok && chatbotResponse.ok) {
        setChatbotMessage('✅ All chatbot settings saved successfully!');
      } else {
        setChatbotMessage('⚠️ Settings saved locally, some backend updates failed');
      }
    } catch (error) {
      console.error('Error saving all chatbot settings:', error);
      setChatbotMessage('⚠️ Settings saved locally, backend update failed');
    } finally {
      setLoading(false);
      setTimeout(() => setChatbotMessage(''), 3000);
    }
  };
  
  // Load settings on mount
  const loadSettings = () => {
    try {
      const savedSettings = localStorage.getItem('quotationSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setShowDraftQuotation(settings.show_draft_quotation ?? true);
        setEmailNotificationEnabled(settings.email_notification_enabled ?? true);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Load chatbot settings
  const loadChatbotSettings = () => {
    try {
      const savedSettings = localStorage.getItem('chatbotSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setRequiredFields(settings.required_fields ?? {
          company_name: true,
          customer_email: true,
          budget_per_pack: true,
          number_of_packs: true,
          occasion: true,
          special_requests: false
        });
        setThankYouNote(settings.thank_you_note ?? 'Thank you for your enquiry! We will get back to you shortly with a customized quotation.');
      }
    } catch (error) {
      console.error('Error loading chatbot settings:', error);
    }
  };

  // Load business units connector settings
  const loadConnectorSettings = () => {
    try {
      const savedSettings = localStorage.getItem('connectorSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setBusinessUnits(settings.business_units ?? businessUnits);
        setRoutingRules(settings.routing_rules ?? routingRules);
      }
    } catch (error) {
      console.error('Error loading connector settings:', error);
    }
  };

  // Save business units connector settings
  const saveConnectorSettings = async () => {
    try {
      setLoading(true);
      const settings = {
        business_units: businessUnits,
        routing_rules: routingRules
      };
      
      // Save to localStorage
      localStorage.setItem('connectorSettings', JSON.stringify(settings));
      
      // Send to backend
      const response = await fetch('/api/admin/connector-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setConnectorMessage('✅ Business units connector settings saved successfully!');
      } else {
        setConnectorMessage('⚠️ Settings saved locally, backend update failed');
      }
    } catch (error) {
      console.error('Error saving connector settings:', error);
      setConnectorMessage('⚠️ Settings saved locally, backend update failed');
    } finally {
      setLoading(false);
      setTimeout(() => setConnectorMessage(''), 3000);
    }
  };

  // Load email settings
  const loadEmailSettings = () => {
    try {
      const savedSettings = localStorage.getItem('emailSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setEmailConfig(settings.email_config ?? emailConfig);
        setEmailTemplates(settings.email_templates ?? emailTemplates);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
    }
  };

  // Fetch email logs
  const fetchEmailLogs = async () => {
    try {
      const response = await fetch('/api/admin/email-logs');
      if (response.ok) {
        const data = await response.json();
        setEmailLogs(data.logs || []);
      } else {
        // Mock data for demo
        setEmailLogs([
          {
            id: 'log_001',
            to: 'sarah@techcorp.com',
            subject: 'Your Custom Quotation is Ready - PrintnGift',
            template: 'quotation_ready',
            status: 'sent',
            timestamp: '2025-08-21 10:30:00',
            quotation_id: 'SUGG-20250821103000'
          },
          {
            id: 'log_002',
            to: 'victor@abcliner.com',
            subject: 'Following up on your quotation - PrintnGift',
            template: 'follow_up',
            status: 'pending',
            timestamp: '2025-08-21 11:15:00',
            quotation_id: 'SUGG-20250821111500'
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching email logs:', error);
      // Set mock data on error
      setEmailLogs([]);
    }
  };

  // Save email settings
  const saveEmailSettings = async () => {
    try {
      setLoading(true);
      const settings = {
        email_config: emailConfig,
        email_templates: emailTemplates
      };
      
      // Save to localStorage
      localStorage.setItem('emailSettings', JSON.stringify(settings));
      
      // Send to backend
      const response = await fetch('/api/admin/email-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setEmailMessage('✅ Email settings saved successfully!');
      } else {
        setEmailMessage('⚠️ Settings saved locally, backend update failed');
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setEmailMessage('⚠️ Settings saved locally, backend update failed');
    } finally {
      setLoading(false);
      setTimeout(() => setEmailMessage(''), 3000);
    }
  };

  // Test email configuration
  const testEmail = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          config: emailConfig,
          test_recipient: emailConfig.sender_email
        })
      });
      
      if (response.ok) {
        setEmailMessage('✅ Test email sent successfully!');
      } else {
        setEmailMessage('❌ Test email failed to send');
      }
    } catch (error) {
      console.error('Error testing email:', error);
      setEmailMessage('❌ Test email failed to send');
    } finally {
      setLoading(false);
      setTimeout(() => setEmailMessage(''), 3000);
    }
  };

  useEffect(() => {
    fetchSuggestions();
    loadSettings();
    loadChatbotSettings();
    loadConnectorSettings();
    loadEmailSettings();
    fetchEmailLogs();
  }, []);

  const tabs = [
    { id: 'chatbot', label: 'Chatbot Management', icon: MessageSquare },
    { id: 'staff-review', label: 'Staff Review', icon: Users },
    { id: 'quotations', label: 'Quotation Management', icon: FileText },
    { id: 'email', label: 'Email Management', icon: Mail },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'expert-system', label: 'Expert System', icon: Brain },
    { id: 'settings', label: 'Connect Order info to Business Units', icon: Building2 },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft size={16} />
                <span>Back to Chat</span>
              </button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">🎯 Sales Assistant Control Center</h1>
              <p className="text-gray-600">Comprehensive management dashboard for all business operations</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500">
              {new Date().toLocaleString()}
            </span>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-600">System Online</span>
            </div>
            {onLogout && (
              <button
                onClick={onLogout}
                className="flex items-center space-x-2 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut size={16} />
                <span className="text-sm">Logout</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar Navigation */}
        <div className="w-64 bg-white border-r border-gray-200 min-h-screen">
          <nav className="p-4">
            <div className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {activeTab === 'staff-review' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">👥 Quotation Suggestions Review</h2>
                <button
                  onClick={fetchSuggestions}
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                  <span>Refresh</span>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Suggestions List */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Pending Suggestions</h3>
                  
                  {suggestions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <AlertCircle size={48} className="mx-auto mb-2 text-gray-300" />
                      <p>No pending suggestions</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {suggestions.map((suggestion) => (
                        <div
                          key={suggestion.suggestion_id}
                          className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                            selectedSuggestion === suggestion.suggestion_id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => {
                            setSelectedSuggestion(suggestion.suggestion_id);
                            viewSuggestionDetails(suggestion.suggestion_id);
                          }}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Building2 size={16} className="text-gray-500" />
                              <span className="font-medium text-gray-900">{suggestion.company}</span>
                            </div>
                            <span className="text-xs text-gray-500 flex items-center">
                              <Clock size={12} className="mr-1" />
                              {suggestion.timestamp}
                            </span>
                          </div>
                          
                          <div className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">Customer:</span> {suggestion.customer_id}
                          </div>
                          
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center text-green-600">
                              <DollarSign size={14} className="mr-1" />
                              {suggestion.budget}
                            </span>
                            <span className="text-gray-500">{suggestion.quantity}</span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              suggestion.status === 'pending_staff_review'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                            }`}>
                              {suggestion.status.replace('_', ' ')}
                            </span>
                          </div>
                          
                          <div className="mt-2 text-xs text-blue-600 font-medium">
                            ID: {suggestion.suggestion_id}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Suggestion Details */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Suggestion Details</h3>
                  
                  {!suggestionDetails ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText size={48} className="mx-auto mb-2 text-gray-300" />
                      <p>Select a suggestion to view details</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Customer Info */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">📋 Customer Information</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p><strong>Customer:</strong> {suggestionDetails.customer}</p>
                          <p><strong>Requirements:</strong> {suggestionDetails.requirements}</p>
                          <p><strong>Budget:</strong> {suggestionDetails.budget}</p>
                          <p><strong>Occasion:</strong> {suggestionDetails.occasion}</p>
                        </div>
                      </div>

                      {/* Strategies */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">🎯 7 AI-Generated Budget Strategies</h4>
                        <div className="space-y-3">
                          {suggestionDetails.strategies.map((strategy, index) => (
                            <div
                              key={index}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedStrategy === index
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              onClick={() => setSelectedStrategy(index)}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <h5 className="font-medium text-gray-900">
                                  {index + 1}. {strategy.name}
                                </h5>
                                <div className="text-right">
                                  <div className="text-sm font-medium text-green-600">{strategy.cost}</div>
                                  {strategy.variance && (
                                    <div className={`text-xs ${
                                      strategy.variance.includes('savings') || strategy.variance.includes('under') 
                                        ? 'text-green-600' 
                                        : strategy.variance.includes('premium') 
                                        ? 'text-orange-600' 
                                        : 'text-blue-600'
                                    }`}>
                                      {strategy.variance}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <p className="text-sm text-gray-600 mb-2">{strategy.description}</p>
                              {strategy.reasoning && (
                                <div className="mb-3 p-2 bg-blue-50 rounded text-xs text-blue-800 border-l-2 border-blue-300">
                                  <strong>💡 Reasoning:</strong> {strategy.reasoning}
                                </div>
                              )}
                              <div className="flex flex-wrap gap-1">
                                {strategy.items.map((item, itemIndex) => (
                                  <span
                                    key={itemIndex}
                                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                                  >
                                    {item}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Accept Button */}
                      <div className="pt-4 border-t border-gray-200">
                        <button
                          onClick={acceptSuggestion}
                          disabled={loading || !selectedSuggestion}
                          className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          <CheckCircle size={16} />
                          <span>Accept & Move to Pipeline</span>
                        </button>
                        
                        {message && (
                          <div className={`mt-3 p-3 rounded-lg text-sm ${
                            message.includes('✅') 
                              ? 'bg-green-100 text-green-800 border border-green-200' 
                              : 'bg-red-100 text-red-800 border border-red-200'
                          }`}>
                            {message}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Business Units Connector Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">🏢 Connect Order info to Business Units</h2>
              
              <div className="space-y-6">
                {/* Business Units Management */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">🏪 Business Units Configuration</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure your business units and their contact information for order routing.
                  </p>
                  
                  <div className="space-y-4">
                    {businessUnits.map((unit, index) => (
                      <div key={unit.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <input
                              type="text"
                              value={unit.name}
                              onChange={(e) => {
                                const newUnits = [...businessUnits];
                                newUnits[index].name = e.target.value;
                                setBusinessUnits(newUnits);
                              }}
                              className="font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:ring-0 px-0"
                            />
                            <Building2 size={16} className="text-gray-400" />
                          </div>
                          <input
                            type="email"
                            value={unit.email}
                            onChange={(e) => {
                              const newUnits = [...businessUnits];
                              newUnits[index].email = e.target.value;
                              setBusinessUnits(newUnits);
                            }}
                            className="text-sm text-gray-600 mt-1 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:ring-0 px-0 w-full"
                            placeholder="unit@company.com"
                          />
                        </div>
                        <div className="flex items-center space-x-3">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={unit.active}
                              onChange={(e) => {
                                const newUnits = [...businessUnits];
                                newUnits[index].active = e.target.checked;
                                setBusinessUnits(newUnits);
                              }}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className={`text-sm font-medium ${unit.active ? 'text-green-700' : 'text-gray-500'}`}>
                              {unit.active ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                          <button
                            onClick={() => {
                              if (businessUnits.length > 1) {
                                setBusinessUnits(businessUnits.filter((_, i) => i !== index));
                              }
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove unit"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    <button
                      onClick={() => {
                        const newId = `unit_${Date.now()}`;
                        setBusinessUnits([...businessUnits, {
                          id: newId,
                          name: 'New Business Unit',
                          email: 'new@company.com',
                          active: true,
                          conditions: []
                        }]);
                      }}
                      className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
                    >
                      + Add New Business Unit
                    </button>
                  </div>
                </div>

                {/* Routing Rules Configuration */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">🎯 Smart Routing Rules</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Configure automatic routing based on order criteria.
                    </p>
                    
                    <div className="space-y-4">
                      {/* Default Unit */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Default Business Unit</label>
                        <select
                          value={routingRules.default_unit}
                          onChange={(e) => setRoutingRules(prev => ({ ...prev, default_unit: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        >
                          {businessUnits.filter(unit => unit.active).map(unit => (
                            <option key={unit.id} value={unit.id}>{unit.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Budget Thresholds */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Budget Thresholds</label>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600 w-20">Premium:</span>
                            <span className="text-sm text-gray-600">$</span>
                            <input
                              type="number"
                              value={routingRules.budget_thresholds.premium}
                              onChange={(e) => setRoutingRules(prev => ({
                                ...prev,
                                budget_thresholds: { ...prev.budget_thresholds, premium: parseInt(e.target.value) }
                              }))}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">/pack</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600 w-20">Corporate:</span>
                            <span className="text-sm text-gray-600">$</span>
                            <input
                              type="number"
                              value={routingRules.budget_thresholds.corporate}
                              onChange={(e) => setRoutingRules(prev => ({
                                ...prev,
                                budget_thresholds: { ...prev.budget_thresholds, corporate: parseInt(e.target.value) }
                              }))}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                            />
                            <span className="text-sm text-gray-600">total</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">📊 Routing Preview</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      See how orders will be routed based on your current settings.
                    </p>
                    
                    <div className="space-y-3">
                      {businessUnits.filter(unit => unit.active).map(unit => (
                        <div key={unit.id} className="p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-gray-900">{unit.name}</h4>
                            <div className={`w-2 h-2 rounded-full ${
                              routingRules.default_unit === unit.id ? 'bg-blue-500' : 'bg-gray-300'
                            }`}></div>
                          </div>
                          <p className="text-xs text-gray-600 mb-1">{unit.email}</p>
                          <div className="text-xs text-gray-500">
                            {routingRules.default_unit === unit.id && <span className="text-blue-600">• Default unit</span>}
                            {unit.id === 'premium' && <span className="text-purple-600">• Budget ≥ ${routingRules.budget_thresholds.premium}/pack</span>}
                            {unit.id === 'corporate' && <span className="text-green-600">• Budget ≥ ${routingRules.budget_thresholds.corporate} total</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="text-sm font-medium text-blue-900 mb-1">Active Units: {businessUnits.filter(unit => unit.active).length}</h4>
                      <p className="text-xs text-blue-700">
                        Orders will be routed automatically based on budget thresholds and occasion types.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Save Connector Configuration</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Apply your business units and routing rules to the order management system.
                      </p>
                    </div>
                    <button
                      onClick={saveConnectorSettings}
                      disabled={loading}
                      className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <Building2 size={16} />
                      <span>{loading ? 'Saving...' : 'Save Connector Settings'}</span>
                    </button>
                  </div>
                  
                  {connectorMessage && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${
                      connectorMessage.includes('✅') 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}>
                      {connectorMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Chatbot Management Tab */}
          {activeTab === 'chatbot' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">🤖 Chatbot Management</h2>
              
              <div className="space-y-6">
                {/* Quotation Display Settings */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">📋 Quotation Display Settings</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Control when and how draft quotations are displayed to clients during chat conversations.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Draft Quotation Control */}
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex-1">
                        <h4 className="font-medium text-blue-900">Show Draft Quotation Panel</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Controls whether the draft quotation panel appears during conversations. 
                          When disabled, clients will only receive quotations via email.
                        </p>
                      </div>
                      <label className="flex items-center space-x-3 ml-4">
                        <input
                          type="checkbox"
                          checked={showDraftQuotation}
                          onChange={(e) => setShowDraftQuotation(e.target.checked)}
                          className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-blue-900">
                          {showDraftQuotation ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>
                    
                    {/* Email Notification Control */}
                    <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex-1">
                        <h4 className="font-medium text-green-900">Email Notifications</h4>
                        <p className="text-sm text-green-700 mt-1">
                          When enabled, clients will be notified that they will receive a formal quotation via email.
                          This helps set proper expectations about the quotation delivery process.
                        </p>
                      </div>
                      <label className="flex items-center space-x-3 ml-4">
                        <input
                          type="checkbox"
                          checked={emailNotificationEnabled}
                          onChange={(e) => setEmailNotificationEnabled(e.target.checked)}
                          className="w-5 h-5 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                        />
                        <span className="text-sm font-medium text-green-900">
                          {emailNotificationEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Current Behavior Display */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Current Behavior Summary</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${showDraftQuotation ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span>
                          Draft quotation panel: {showDraftQuotation ? 'Will be shown to clients' : 'Hidden from clients'}
                        </span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${emailNotificationEnabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span>
                          Email notifications: {emailNotificationEnabled ? 'Clients will be notified about email quotations' : 'No email notification messages'}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Required Information Fields */}
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">📋 Required Information for Quotations</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Configure which information fields Mary must collect from customers before generating quotations.
                    </p>
                  
                  <div className="space-y-4">
                    {Object.entries(requiredFields).map(([field, enabled]) => {
                      const fieldLabels = {
                        company_name: 'Company Name',
                        customer_email: 'Customer Email',
                        budget_per_pack: 'Budget per Pack',
                        number_of_packs: 'Number of Packs/Quantity',
                        occasion: 'Occasion/Event Type',
                        special_requests: 'Special Requests/Requirements'
                      };
                      
                      const fieldDescriptions = {
                        company_name: 'Customer\'s company or organization name',
                        customer_email: 'Valid email address for quotation delivery',
                        budget_per_pack: 'Budget range or specific amount per package',
                        number_of_packs: 'Total quantity needed for the event',
                        occasion: 'Type of event (corporate, festival, etc.)',
                        special_requests: 'Custom branding, special packaging, etc.'
                      };

                      return (
                        <div key={field} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{fieldLabels[field as keyof typeof fieldLabels]}</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {fieldDescriptions[field as keyof typeof fieldDescriptions]}
                            </p>
                          </div>
                          <label className="flex items-center space-x-3">
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={(e) => setRequiredFields(prev => ({ ...prev, [field]: e.target.checked }))}
                              className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className={`text-sm font-medium ${enabled ? 'text-green-700' : 'text-gray-500'}`}>
                              {enabled ? 'Required' : 'Optional'}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-medium text-blue-900 mb-2">📊 Current Requirements Summary</h4>
                    <div className="text-sm text-blue-700">
                      <p><strong>Required fields:</strong> {Object.entries(requiredFields).filter(([_, enabled]) => enabled).length} out of {Object.keys(requiredFields).length}</p>
                      <p><strong>Mary will ask for:</strong> {Object.entries(requiredFields)
                        .filter(([_, enabled]) => enabled)
                        .map(([field]) => field.replace('_', ' '))
                        .join(', ')}</p>
                    </div>
                  </div>
                </div>

                {/* Thank You Message Settings */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">💌 Thank You Enquiry Message</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Customize the message Mary sends to customers after collecting their quotation requirements.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="thankYouNote" className="block text-sm font-medium text-gray-700 mb-2">
                        Thank You Message
                      </label>
                      <textarea
                        id="thankYouNote"
                        value={thankYouNote}
                        onChange={(e) => setThankYouNote(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter the message Mary will send to customers..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This message will be sent after Mary has collected all required information from the customer.
                      </p>
                    </div>

                    {/* Message Preview */}
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <h4 className="font-medium text-purple-900 mb-2">📱 Message Preview</h4>
                      <div className="bg-white p-3 rounded-lg border border-purple-200 text-sm">
                        <div className="flex items-start space-x-2">
                          <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                            M
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-purple-700">Mary - Sales Assistant</p>
                            <p className="text-gray-700 mt-1">{thankYouNote}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Save Chatbot Configuration</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Apply your changes to Mary's behavior, display settings, and message templates.
                      </p>
                    </div>
                    <button
                      onClick={saveAllChatbotSettings}
                      disabled={loading}
                      className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                    >
                      <MessageSquare size={16} />
                      <span>{loading ? 'Saving...' : 'Save All Settings'}</span>
                    </button>
                  </div>
                  
                  {chatbotMessage && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${
                      chatbotMessage.includes('✅') 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}>
                      {chatbotMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Email Management Tab */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">📧 Email Management</h2>
              
              <div className="space-y-6">
                {/* Email Configuration */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">⚙️ Email Configuration</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure SMTP settings and sender information for outgoing emails.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Server</label>
                        <input
                          type="text"
                          value={emailConfig.smtp_server}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_server: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          placeholder="smtp.gmail.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
                        <input
                          type="number"
                          value={emailConfig.smtp_port}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          placeholder="587"
                        />
                      </div>
                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={emailConfig.use_tls}
                            onChange={(e) => setEmailConfig(prev => ({ ...prev, use_tls: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Use TLS/SSL</span>
                        </label>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sender Email</label>
                        <input
                          type="email"
                          value={emailConfig.sender_email}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, sender_email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          placeholder="mary@printngift.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sender Name</label>
                        <input
                          type="text"
                          value={emailConfig.sender_name}
                          onChange={(e) => setEmailConfig(prev => ({ ...prev, sender_name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Mary - PrintnGift Sales Assistant"
                        />
                      </div>
                      <button
                        onClick={testEmail}
                        disabled={loading}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        <Mail size={16} />
                        <span>{loading ? 'Testing...' : 'Test Configuration'}</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Email Templates */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">📝 Email Templates</h3>
                    <button
                      onClick={() => {
                        const newTemplate = {
                          id: `template_${Date.now()}`,
                          name: 'New Template',
                          subject: 'Subject Line',
                          content: 'Dear {{customer_name}},\n\nYour message here.\n\nBest regards,\nMary'
                        };
                        setEmailTemplates([...emailTemplates, newTemplate]);
                      }}
                      className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <FileText size={16} />
                      <span>Add Template</span>
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mb-6">
                    Create and manage email templates for different purposes. Use {`{{customer_name}}`}, {`{{company_name}}`}, and {`{{quotation_id}}`} as placeholders.
                  </p>
                  
                  <div className="space-y-4">
                    {emailTemplates.map((template, index) => (
                      <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <input
                            type="text"
                            value={template.name}
                            onChange={(e) => {
                              const newTemplates = [...emailTemplates];
                              newTemplates[index].name = e.target.value;
                              setEmailTemplates(newTemplates);
                            }}
                            className="font-medium text-gray-900 bg-transparent border-0 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:ring-0 px-0"
                          />
                          <button
                            onClick={() => {
                              if (emailTemplates.length > 1) {
                                setEmailTemplates(emailTemplates.filter((_, i) => i !== index));
                              }
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove template"
                          >
                            <X size={16} />
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                            <input
                              type="text"
                              value={template.subject}
                              onChange={(e) => {
                                const newTemplates = [...emailTemplates];
                                newTemplates[index].subject = e.target.value;
                                setEmailTemplates(newTemplates);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Content</label>
                            <textarea
                              value={template.content}
                              onChange={(e) => {
                                const newTemplates = [...emailTemplates];
                                newTemplates[index].content = e.target.value;
                                setEmailTemplates(newTemplates);
                              }}
                              rows={6}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Dear {{customer_name}}..."
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Available placeholders: {`{{customer_name}}`}, {`{{company_name}}`}, {`{{quotation_id}}`}, {`{{budget}}`}, {`{{occasion}}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Email Logs */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-gray-900">📊 Email Activity</h3>
                      <button
                        onClick={fetchEmailLogs}
                        disabled={loading}
                        className="flex items-center space-x-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
                      >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        <span>Refresh</span>
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mb-6">
                      Recent email activity and delivery status.
                    </p>
                    
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {emailLogs.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <Mail size={48} className="mx-auto mb-2 text-gray-300" />
                          <p>No email logs found</p>
                        </div>
                      ) : (
                        emailLogs.map((log: any) => (
                          <div key={log.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <Mail size={14} className="text-gray-500" />
                                <span className="text-sm font-medium text-gray-900">{log.to}</span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                log.status === 'sent' 
                                  ? 'bg-green-100 text-green-800' 
                                  : log.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {log.status}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-1">{log.subject}</p>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Template: {log.template}</span>
                              <span>{log.timestamp}</span>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">📈 Email Statistics</h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="text-2xl font-bold text-blue-900">{emailLogs.length}</div>
                          <div className="text-sm text-blue-700">Total Emails</div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="text-2xl font-bold text-green-900">
                            {emailLogs.filter((log: any) => log.status === 'sent').length}
                          </div>
                          <div className="text-sm text-green-700">Successfully Sent</div>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-2">📧 Current Configuration</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• Server: {emailConfig.smtp_server}:{emailConfig.smtp_port}</li>
                          <li>• From: {emailConfig.sender_name} &lt;{emailConfig.sender_email}&gt;</li>
                          <li>• Templates: {emailTemplates.length} configured</li>
                          <li>• Security: {emailConfig.use_tls ? 'TLS Enabled' : 'TLS Disabled'}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Save Email Configuration</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Save your email configuration and templates to the system.
                      </p>
                    </div>
                    <button
                      onClick={saveEmailSettings}
                      disabled={loading}
                      className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      <Mail size={16} />
                      <span>{loading ? 'Saving...' : 'Save Email Settings'}</span>
                    </button>
                  </div>
                  
                  {emailMessage && (
                    <div className={`mt-4 p-3 rounded-lg text-sm ${
                      emailMessage.includes('✅') 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                    }`}>
                      {emailMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Other tabs - placeholder content */}
          {activeTab !== 'staff-review' && activeTab !== 'settings' && activeTab !== 'chatbot' && activeTab !== 'email' && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {tabs.find(tab => tab.id === activeTab)?.label}
              </h2>
              <div className="text-center py-12 text-gray-500">
                <div className="mb-4">
                  {React.createElement(tabs.find(tab => tab.id === activeTab)?.icon || Settings, { size: 64, className: "mx-auto text-gray-300" })}
                </div>
                <p className="text-lg">This module is under development</p>
                <p className="text-sm">Will be implemented with the same TypeScript consistency</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};